import MenuSheetItem from "@coworker/apprestructured/src/task-management/components/MenuSheet/MenuSheetItem/MenuSheetItem";
import MenuSheet from "@coworker/apprestructured/src/task-management/components/MenuSheet/MenuSheet";
import { JSX, memo } from "react";
import { useTranslation } from "@coworker/locales";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { CloseReasonOption } from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseSheet";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

interface TaskViewerMenuSheetProps {
  showMenuSheet: boolean;
  taskState: TaskState;
  isTaskScheduled: boolean;
  assignedUserId: string;
  isDisabled: boolean;
  isEditDisabled: boolean;
  closeReasonOptions: CloseReasonOption[];
  onMenuCloseButtonClick: () => void;
  onEditTaskMenuSheetClick: () => void;
  onDropTaskMenuSheetClick: () => void;
  onCloseTaskMenuSheetClick: () => void;
  onReopenClosedTaskMenuSheetClick: () => void;
  onReopenCompletedTaskMenuSheetClick: () => void;
  onCloseAllUnfinishedTasksMenuSheetClick: () => void;
}

export const TaskViewerMenuSheet = memo((props: TaskViewerMenuSheetProps) => {
  const {
    showMenuSheet,
    isDisabled,
    taskState,
    isTaskScheduled,
    isEditDisabled,
    assignedUserId,
    closeReasonOptions,
    onMenuCloseButtonClick,
    onCloseTaskMenuSheetClick,
    onDropTaskMenuSheetClick,
    onEditTaskMenuSheetClick,
    onReopenClosedTaskMenuSheetClick,
    onReopenCompletedTaskMenuSheetClick,
    onCloseAllUnfinishedTasksMenuSheetClick,
  } = props;
  const { t } = useTranslation();
  const myUserId = useUID();
  const enableRemoveReopenAction = useFlag(FLAGS.ENABLE_REMOVE_REOPEN_ACTION);
  const items: JSX.Element[] = [];

  if (taskState !== "closed" && taskState !== "completed") {
    items.push(
      <MenuSheetItem
        testId="task-viewer-menu-sheet-edit-button"
        key="edit"
        title={t("editTaskString")}
        disabled={isDisabled || isEditDisabled}
        onClick={onEditTaskMenuSheetClick}
      />
    );
  }
  if (taskState === "pending" && myUserId === assignedUserId) {
    items.push(
      <MenuSheetItem
        testId="task-viewer-menu-sheet-drop-button"
        key="drop"
        title={t("dropString")}
        disabled={isDisabled}
        onClick={onDropTaskMenuSheetClick}
      />
    );
  }
  if (taskState === "pending") {
    items.push(
      <MenuSheetItem
        testId="task-viewer-menu-sheet-close-button"
        key="close"
        title={t("closeString")}
        disabled={isDisabled || closeReasonOptions.length === 0}
        onClick={onCloseTaskMenuSheetClick}
      />
    );
    if (isTaskScheduled) {
      items.push(
        <MenuSheetItem
          testId="task-viewer-menu-sheet-close-all-button"
          key="close-all"
          title={t("closeAllUnfinishedTasksString")}
          disabled={isDisabled || closeReasonOptions.length === 0}
          onClick={onCloseAllUnfinishedTasksMenuSheetClick}
        />
      );
    }
  }
  if (!enableRemoveReopenAction && taskState === "closed") {
    items.push(
      <MenuSheetItem
        testId="task-viewer-menu-sheet-reopen-closed-button"
        key="reopen-closed"
        title={t("reopenString")}
        disabled={isDisabled}
        onClick={onReopenClosedTaskMenuSheetClick}
      />
    );
  }
  if (!enableRemoveReopenAction && taskState === "completed") {
    items.push(
      <MenuSheetItem
        testId="task-viewer-menu-sheet-reopen-completed-button"
        key="reopen-completed"
        title={t("reopenString")}
        disabled={isDisabled}
        onClick={onReopenCompletedTaskMenuSheetClick}
      />
    );
  }

  return (
    <MenuSheet
      testId="task-viewer-menu-sheet"
      onCloseClick={onMenuCloseButtonClick}
      visible={showMenuSheet}
    >
      {items}
    </MenuSheet>
  );
});
